// Messages Manager Component
import React, { useState, useEffect } from 'react';
import { Trash2, Loader2, AlertTriangle, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_MESSAGES = 100;

const MessagesManager = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { fetchContacts(); }, []);

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/contacts`, { credentials: 'include' });
      if (response.ok) { const data = await response.json(); setContacts(data || []); }
    } catch (err) { console.error('Error fetching contacts:', err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce message ?')) return;
    try {
      await fetch(`${BACKEND_URL}/api/admin/contacts/${id}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Message supprimé');
      fetchContacts();
    } catch (err) { 
      console.error('Error deleting contact:', err); 
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`⚠️ Êtes-vous sûr de vouloir supprimer TOUS les ${contacts.length} messages ?\n\nCette action est irréversible !`)) return;
    
    setDeleting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/contacts`, { 
        method: 'DELETE', 
        credentials: 'include' 
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchContacts();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (err) { 
      console.error('Error deleting all contacts:', err); 
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-light text-slate-800">Messages de Contact</h2>
          <p className="text-sm text-slate-500">
            {contacts.length} message{contacts.length !== 1 ? 's' : ''} 
            <span className="text-slate-400"> (max {MAX_MESSAGES} conservés)</span>
          </p>
        </div>
        {contacts.length > 0 && (
          <Button 
            variant="outline" 
            onClick={handleDeleteAll}
            disabled={deleting}
            className="text-red-600 border-red-300 hover:bg-red-50"
          >
            {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Tout supprimer
          </Button>
        )}
      </div>

      {/* Warning if approaching limit */}
      {contacts.length >= MAX_MESSAGES * 0.8 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-amber-800 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            <strong>Attention :</strong> Vous approchez de la limite de {MAX_MESSAGES} messages. 
            Les anciens messages seront automatiquement supprimés.
          </span>
        </div>
      )}

      <div className="space-y-4">
        {contacts.map((contact) => (
          <div key={contact.id} className="bg-white border border-slate-200 rounded-xl p-4 hover:shadow-sm transition-shadow">
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <h3 className="font-medium text-slate-800">{contact.name}</h3>
                  <span className="text-sm text-slate-500 truncate">&lt;{contact.email}&gt;</span>
                </div>
                <p className="text-sm font-medium text-slate-700 mb-2">{contact.subject}</p>
                <p className="text-sm text-slate-600 whitespace-pre-wrap">{contact.message}</p>
                <p className="text-xs text-slate-400 mt-2">{new Date(contact.created_at).toLocaleString('fr-FR')}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(contact.id)} title="Supprimer">
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      {contacts.length === 0 && (
        <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-xl">
          <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p>Aucun message.</p>
        </div>
      )}
    </div>
  );
};

export default MessagesManager;
